import { SCREENING_LISTS } from "@platform/contracts";
import { Label, Textarea } from "@platform/ui";
import { useTranslations } from "use-intl";

/** The shortest reason for a possible match, as the API asks (ADR 0026). */
export const MIN_MATCH_REASON = 20;

export interface ScreeningDraft {
  readonly lists: readonly (typeof SCREENING_LISTS)[number][];
  readonly result: "clear" | "possible_match";
  readonly reason: string;
}

export const EMPTY_SCREENING: ScreeningDraft = { lists: [], result: "clear", reason: "" };

/** Every list checked, and a possible match explained. */
export const screeningReady = (draft: ScreeningDraft) =>
  SCREENING_LISTS.every((list) => draft.lists.includes(list)) &&
  (draft.result === "clear" || draft.reason.trim().length >= MIN_MATCH_REASON);

/** The screening as the API takes it. */
export const screeningBody = (draft: ScreeningDraft) => ({
  lists: [...draft.lists],
  result: draft.result,
  ...(draft.reason.trim() === "" ? {} : { reason: draft.reason.trim() }),
});

/**
 * A sanctions screening of the company and its directors (ADR 0026): each list ticked once checked, the result, and,
 * for a possible match, why it does not stop the enterprise. Used to approve a case verified by hand, and to screen
 * a verified enterprise again.
 */
export function ScreeningFields({
  id,
  value,
  onChange,
}: {
  readonly id: string;
  readonly value: ScreeningDraft;
  readonly onChange: (next: ScreeningDraft) => void;
}) {
  const t = useTranslations();
  const toggle = (list: (typeof SCREENING_LISTS)[number], checked: boolean) =>
    onChange({
      ...value,
      lists: checked ? [...value.lists, list] : value.lists.filter((item) => item !== list),
    });
  return (
    <div className="flex flex-col gap-3">
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-small font-medium text-ink">{t("admin.screening.lists")}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-small text-ink">
          {SCREENING_LISTS.map((list) => (
            <label key={list} className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                className="size-4 accent-brand-600"
                checked={value.lists.includes(list)}
                onChange={(event) => toggle(list, event.target.checked)}
              />
              {t(`admin.screening.list.${list}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 text-small font-medium text-ink">{t("admin.screening.result")}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-small text-ink">
          {(["clear", "possible_match"] as const).map((result) => (
            <label key={result} className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name={`${id}-result`}
                className="size-4 accent-brand-600"
                checked={value.result === result}
                onChange={() => onChange({ ...value, result })}
              />
              {t(`admin.screening.${result}`)}
            </label>
          ))}
        </div>
      </fieldset>
      {value.result === "possible_match" && (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={`${id}-reason`}>{t("admin.screening.reason")}</Label>
          <Textarea
            id={`${id}-reason`}
            rows={3}
            value={value.reason}
            onChange={(event) => onChange({ ...value, reason: event.target.value })}
          />
          <p className="text-caption text-ink-muted">
            {t("admin.screening.reasonHint", { min: MIN_MATCH_REASON })}
          </p>
        </div>
      )}
    </div>
  );
}
